/**
 * 사용자/기관 등 API가 문자열 또는 로케일 맵·중첩 객체 형태로 필드를 줄 때
 * 화면에 쓸 단일 문자열로 만든다. (React에 객체를 그대로 넣으면 "[object Object]"로 보임)
 */
import { BULK_CREATE_GUEST_VALIDITY_DAYS_DEFAULT } from '@/utils/bulkCreateGuestValidity';

const PREFERRED_KEYS = [
	'ko',
	'en',
	'default',
	'value',
	'name',
	'title',
	'text',
	'realName',
	'userName',
	'displayName',
	'fullName',
	'nickName',
	'givenName',
	'familyName',
	'loginId',
	'label',
] as const;

/** JS에서 객체를 잘못 문자열화했을 때 저장되는 값 (엑셀 일괄등록 등) */
export function isCorruptedSerializedObjectString(value: string): boolean {
	return /^\[object\s+Object\]$/i.test(value.trim());
}

export function apiValueToDisplayString(value: unknown, preferredLocale?: string, depth = 0): string {
	if (value == null) return '';
	if (depth > 8) return '';
	if (typeof value === 'string') {
		if (isCorruptedSerializedObjectString(value)) return '';
		return value;
	}
	if (typeof value === 'number' || typeof value === 'boolean') return String(value);

	if (Array.isArray(value)) {
		for (const item of value) {
			const s = apiValueToDisplayString(item, preferredLocale, depth + 1);
			if (s.trim()) return s;
		}
		return '';
	}

	if (typeof value === 'object') {
		const o = value as Record<string, unknown>;
		const shortLang = preferredLocale?.split('-')[0];
		if (shortLang) {
			const loc = o[shortLang];
			const s = apiValueToDisplayString(loc, preferredLocale, depth + 1);
			if (s.trim()) return s;
		}
		for (const k of PREFERRED_KEYS) {
			const s = apiValueToDisplayString(o[k], preferredLocale, depth + 1);
			if (s.trim()) return s;
		}
		for (const v of Object.values(o)) {
			const s = apiValueToDisplayString(v, preferredLocale, depth + 1);
			if (s.trim()) return s;
		}
	}

	return '';
}

/** 테이블 등: User.name 이 변환 누락으로 객체로 남아도 안전하게 문자열만 렌더 */
export function safeUserDisplayText(
	value: unknown,
	fallback: string,
	locale?: string
): string {
	if (value == null || value === '') return fallback;
	let out: string;
	if (typeof value === 'string') {
		out = value.trim();
	} else {
		out = apiValueToDisplayString(value, locale).trim();
	}
	if (!out || isCorruptedSerializedObjectString(out)) return fallback;
	return out;
}

/** `/user/option/list` 등 UserInfoDto에 스펙 외로 붙을 수 있는 게스트 만료 필드 후보 */
const GUEST_EXPIRATION_KEYS = [
	'guestAccountExpiresAt',
	'guest_account_expires_at',
	'guestExpiresAt',
	'guest_expires_at',
	'associateExpiresAt',
	'associate_expires_at',
	'accountExpiresAt',
	'account_expires_at',
	'expiresAt',
	'expires_at',
	'validUntil',
	'valid_until',
] as const;

/** YYYY-MM-DD에 일수를 더한 날짜(로컬 달력 기준) */
export function addCalendarDaysToYmd(yyyyMmDd: string, days: number): string {
	const t = yyyyMmDd.trim();
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(t);
	if (!m) return yyyyMmDd;
	const y = Number(m[1]);
	const mo = Number(m[2]);
	const d = Number(m[3]);
	if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return yyyyMmDd;
	const dt = new Date(y, mo - 1, d);
	dt.setDate(dt.getDate() + days);
	const yy = dt.getFullYear();
	const mm = String(dt.getMonth() + 1).padStart(2, '0');
	const dd = String(dt.getDate()).padStart(2, '0');
	return `${yy}-${mm}-${dd}`;
}

/**
 * 관리자 유저 목록 DTO(raw)에서 게스트 만료 시각 문자열 추출.
 * OpenAPI UserInfoDto에 없을 수 있으나 실서버가 내려주면 표시·편집 모달에 사용.
 */
export function extractGuestExpirationFromUserListRaw(
	raw: Record<string, unknown>,
	locale?: string
): string | undefined {
	for (const k of GUEST_EXPIRATION_KEYS) {
		const s = apiValueToDisplayString(raw[k], locale).trim();
		if (s) return s;
	}
	return undefined;
}

/** 편집 폼(YYYY-MM-DD)용 */
export function toExpirationDateInputValue(isoOrDate: string | undefined): string {
	if (!isoOrDate?.trim()) return '';
	const t = isoOrDate.trim();
	if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
	// ISO 8601 with T separator: "2026-04-16T23:59:59..."
	if (/^\d{4}-\d{2}-\d{2}T/.test(t)) return t.slice(0, 10);
	// MySQL/PostgreSQL datetime with space separator: "2026-04-16 23:59:59"
	if (/^\d{4}-\d{2}-\d{2} /.test(t)) return t.slice(0, 10);
	const ms = Date.parse(t);
	if (Number.isNaN(ms)) return t;
	return new Date(ms).toISOString().slice(0, 10);
}

/**
 * 로컬 달력 기준: 만료일 당일까지 유효, **익일**부터 만료로 표시.
 * `ymd`는 `guestExpirationYmdForUserForm` 결과(YYYY-MM-DD)와 동일 형식을 가정.
 */
export function isGuestExpirationDatePassed(ymd: string | undefined | null): boolean {
	if (!ymd?.trim()) return false;
	const d = ymd.trim().slice(0, 10);
	if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return false;
	const now = new Date();
	const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
	return todayStr > d;
}

/** 게스트(ASSOCIATE)일 때만 만료일을 YYYY-MM-DD로 정규화 (목록 DTO·스네이크 키 대응) */
export function guestExpirationYmdForUserForm(input: {
	isGuest: boolean;
	guestAccountExpiresAt?: string | null;
	/** 가입·생성일 — API에 만료가 없을 때 생성일 + 기본유효일(30일) 계산에 사용 */
	createdAt?: string | null;
	raw?: Record<string, unknown>;
	locale?: string;
}): string | undefined {
	if (!input.isGuest) return undefined;
	const fromDto = input.guestAccountExpiresAt?.trim();
	if (fromDto) return toExpirationDateInputValue(fromDto);
	if (input.raw) {
		const ext = extractGuestExpirationFromUserListRaw(input.raw, input.locale);
		if (ext) return toExpirationDateInputValue(ext);
	}
	const created = input.createdAt?.trim();
	if (created) {
		const baseYmd = toExpirationDateInputValue(created);
		if (baseYmd) {
			return addCalendarDaysToYmd(baseYmd, BULK_CREATE_GUEST_VALIDITY_DAYS_DEFAULT);
		}
	}
	return undefined;
}
